import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, Lock, FileText, Trash2, ArrowLeft, Save, 
  Loader2, AlertTriangle, Clock, CreditCard, Eye, EyeOff
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_contract-cleaner/artifacts/7lw9nr1y_logo%20l%27effaceur.jpeg";

export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  // V5.44 : first_name/last_name séparés + email affiché
  const [formData, setFormData] = useState({ first_name: '', last_name: '', phone: '', address: '' });
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false });
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchProfile();
  }, []);
  
  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }
      
      const res = await axios.get(`${API}/profile`, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      setProfile(res.data);
      // V5.44 : Charger first_name/last_name
      setFormData({
        first_name: res.data.user?.first_name || (res.data.user?.name?.split(' ')[0] || ''),
        last_name: res.data.user?.last_name || (res.data.user?.name?.split(' ').slice(1).join(' ') || ''),
        phone: res.data.user?.phone || '',
        address: res.data.user?.address || ''
      });
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/profile`, formData, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Profil mis à jour !');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };
  
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new !== passwordData.confirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordData.new.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/profile/change-password`, {
        current_password: passwordData.current,
        new_password: passwordData.new
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Mot de passe modifié !');
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/profile/delete-account`, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      localStorage.removeItem('token');
      toast.success('Compte supprimé définitivement');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setDeleting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
      </div>
    );
  }
  
  const tabs = [
    { id: 'info', label: 'Informations', icon: User },
    { id: 'password', label: 'Mot de passe', icon: Lock },
    { id: 'invoices', label: 'Factures', icon: FileText },
    { id: 'danger', label: 'Zone Danger', icon: Trash2 }
  ];
  
  return (
    <div className="min-h-screen bg-void p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour
            </Button>
          </Link>
          <img src={LOGO_URL} alt="L'Effaceur" className="h-10" />
        </div>
        
        <div className="glass-card border border-gray-800">
          {/* Profile Header - V5.44 : Affichage first_name + last_name */}
          <div className="p-6 border-b border-gray-800">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-cyan-400/20 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-cyan-400" />
              </div>
              <div>
                <h1 className="font-orbitron text-2xl font-bold text-white">
                  {profile?.user?.first_name} {profile?.user?.last_name}
                </h1>
                <p className="text-cyan-400 font-mono text-sm">{profile?.user?.email}</p>
                {profile?.user?.phone && (
                  <p className="text-gray-400 font-mono text-xs mt-1">{profile?.user?.phone}</p>
                )}
              </div>
            </div>
            
            {/* Time Saved Counter */}
            {profile?.stats?.time_saved_hours > 0 && (
              <div className="mt-4 p-4 bg-success/10 border border-success/30 rounded-lg flex items-center gap-3">
                <Clock className="w-6 h-6 text-success" />
                <div>
                  <p className="text-success font-orbitron text-lg font-bold">
                    {profile.stats.time_saved_hours}h économisées
                  </p>
                  <p className="text-gray-400 text-xs">grâce à l'automate L'Effaceur</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Tabs */}
          <div className="flex border-b border-gray-800 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-mono text-sm whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-400/5'
                    : tab.id === 'danger'
                    ? 'text-red-400 hover:bg-red-400/5'
                    : 'text-gray-400 hover:bg-gray-800'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Tab Content */}
          <div className="p-6">
            {/* Informations Tab - V5.44 : Prénom/Nom séparés + Email affiché */}
            {activeTab === 'info' && (
              <motion.form
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onSubmit={handleUpdateProfile}
                className="space-y-6"
              >
                {/* V5.44 : Email (lecture seule) */}
                <div>
                  <Label className="text-gray-400">Email (non modifiable)</Label>
                  <Input
                    value={profile?.user?.email || ''}
                    disabled
                    className="bg-gray-900/50 border-gray-700 text-gray-400 mt-1 cursor-not-allowed"
                    data-testid="profile-email-display"
                  />
                </div>
                
                {/* V5.44 : Prénom et Nom séparés */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400">Prénom *</Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="Votre prénom"
                      className="bg-gray-900 border-gray-700 text-white mt-1"
                      data-testid="profile-firstname-input"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Nom de famille *</Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder="Votre nom"
                      className="bg-gray-900 border-gray-700 text-white mt-1"
                      data-testid="profile-lastname-input"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-gray-400">Téléphone *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+33 6 00 00 00 00"
                    className="bg-gray-900 border-gray-700 text-white mt-1"
                    data-testid="profile-phone-input"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Adresse</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 rue Example, 31000 Toulouse"
                    className="bg-gray-900 border-gray-700 text-white mt-1"
                    data-testid="profile-address-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-cyan-400 text-black hover:bg-cyan-300 w-full md:w-auto"
                  data-testid="save-profile-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </motion.form>
            )}
            
            {/* Password Tab */}
            {activeTab === 'password' && (
              <motion.form
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onSubmit={handleChangePassword}
                className="space-y-6 max-w-md"
              >
                <div className="relative">
                  <Label className="text-gray-400">Mot de passe actuel</Label>
                  <Input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.current}
                    onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                    className="bg-gray-900 border-gray-700 text-white mt-1 pr-10"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className="absolute right-3 top-8 text-gray-400"
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="relative">
                  <Label className="text-gray-400">Nouveau mot de passe</Label>
                  <Input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.new}
                    onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                    className="bg-gray-900 border-gray-700 text-white mt-1 pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className="absolute right-3 top-8 text-gray-400"
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div>
                  <Label className="text-gray-400">Confirmer le nouveau mot de passe</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                    className="bg-gray-900 border-gray-700 text-white mt-1"
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={saving || !passwordData.current || !passwordData.new}
                  className="bg-cyan-400 text-black hover:bg-cyan-300"
                  data-testid="change-password-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                  Changer le mot de passe
                </Button>
              </motion.form>
            )}
            
            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                <h3 className="font-orbitron text-lg text-white mb-4">Historique des Factures</h3>
                {profile?.payments?.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Aucune facture</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {profile?.payments?.map((payment, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-gray-900/50 border border-gray-700 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{payment.pack_type?.replace(/_/g, ' ')}</p>
                          <p className="text-gray-500 text-xs">{new Date(payment.created_at).toLocaleDateString('fr-FR')}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-cyan-400 font-orbitron font-bold">{payment.price}€</p>
                          <span className={`text-xs px-2 py-1 ${payment.status === 'active' ? 'bg-success/20 text-success' : 'bg-gray-700 text-gray-400'}`}>
                            {payment.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
            
            {/* Danger Zone Tab */}
            {activeTab === 'danger' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="p-6 border-2 border-red-600/50 bg-red-600/5 rounded-lg">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0" />
                    <div>
                      <h3 className="font-orbitron text-xl text-red-400 font-bold mb-2">Zone Danger</h3>
                      <p className="text-gray-400 mb-4">
                        La suppression de votre compte est <strong className="text-red-400">irréversible</strong>. 
                        Toutes vos données, documents, mandats et historique seront définitivement effacés.
                      </p>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" className="bg-red-600 hover:bg-red-500 text-white" data-testid="delete-account-btn">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer mon compte et mes données
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-void border border-red-600">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-red-400 font-orbitron">
                              ⚠️ SUPPRESSION DÉFINITIVE
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-gray-400">
                              Cette action est <strong>irréversible</strong>. Votre compte, tous vos documents, 
                              mandats signés et historique seront supprimés définitivement. 
                              Êtes-vous absolument certain ?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-transparent border border-gray-600 text-gray-400">
                              Annuler
                            </AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={handleDeleteAccount}
                              disabled={deleting}
                              className="bg-red-600 text-white hover:bg-red-500"
                            >
                              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                              OUI, SUPPRIMER TOUT
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
